
import React from "react";
import PageLayout from "@/components/Layout/PageLayout";
import { HeroBanner } from "@/components/ui/hero-banner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Users, 
  Building, 
  FileText, 
  ClipboardList, 
  ShieldCheck,
  Search,
  CheckSquare
} from "lucide-react";

const InsurancePage: React.FC = () => {
  return (
    <PageLayout>
      <HeroBanner
        title="Insurance Provider Portal"
        description="Manage claims, verify coverage, and administer insurance benefits"
        backgroundColor="bg-gradient-to-r from-blue-700 to-blue-900"
      />

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="claims">Claims</TabsTrigger>
            <TabsTrigger value="providers">Providers</TabsTrigger>
            <TabsTrigger value="plans">Plans</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Active Members</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">12,845</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">+234 from last month</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Claims Processed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">1,245</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">This month</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Network Providers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Building className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">856</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">58 hospitals, 798 physicians</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Average Claim Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <ClipboardList className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">$345.78</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">$41.2K total processed</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Claims Overview</CardTitle>
                  <CardDescription>Status of recent claims</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600">78%</div>
                      <p className="text-sm text-gray-500 mt-1">Approved</p>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-600">16%</div>
                      <p className="text-sm text-gray-500 mt-1">Pending</p>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-3xl font-bold text-red-600">6%</div>
                      <p className="text-sm text-gray-500 mt-1">Denied</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">Primary Care</span>
                        <span className="text-sm text-gray-500">42% of claims</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '42%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">Specialist Care</span>
                        <span className="text-sm text-gray-500">28% of claims</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '28%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">Emergency</span>
                        <span className="text-sm text-gray-500">15% of claims</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '15%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">Prescriptions</span>
                        <span className="text-sm text-gray-500">10% of claims</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '10%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">Other</span>
                        <span className="text-sm text-gray-500">5% of claims</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '5%' }}></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activities</CardTitle>
                  <CardDescription>Latest updates and actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      "New member enrollment processed",
                      "Claim #12345 approved for payment",
                      "Provider credentialing updated",
                      "Monthly premium report generated",
                      "New plan setup completed"
                    ].map((activity, i) => (
                      <div key={i} className="flex items-start">
                        <div className="mr-2 mt-0.5">
                          <CheckSquare className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm">{activity}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(Date.now() - i * 2 * 60 * 60 * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle>Member Directory</CardTitle>
                <CardDescription>View and manage insured members</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                    <input 
                      type="text" 
                      placeholder="Search members..." 
                      className="w-full rounded-md border border-gray-300 pl-9 pr-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                    Search
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    Advanced
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium">Member ID</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Plan</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Effective Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Recent Claims</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">M-{1000000 + i}</td>
                          <td className="px-4 py-3 text-sm">Member Name {i + 1}</td>
                          <td className="px-4 py-3 text-sm">
                            {["Gold PPO", "Silver HMO", "Bronze EPO", "Platinum PPO", "Gold HMO"][i % 5]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(Date.now() - i * 90 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${i % 5 === 0 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>
                              {i % 5 === 0 ? "Pending" : "Active"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">{i % 5} in last 30 days</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">View</button>
                              <button className="text-green-600 hover:text-green-800 font-medium">Edit</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing 1-10 of 12,845 members
                  </div>
                  <div className="flex space-x-1">
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">Previous</button>
                    <button className="px-3 py-1 bg-blue-600 text-white rounded-md">1</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">2</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">3</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">Next</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="claims">
            <Card>
              <CardHeader>
                <CardTitle>Claims Management</CardTitle>
                <CardDescription>Process and track insurance claims</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-blue-50 border-blue-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">New Claims</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">128</div>
                      <p className="text-xs text-muted-foreground mt-1">Awaiting review</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-yellow-50 border-yellow-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">In Process</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">85</div>
                      <p className="text-xs text-muted-foreground mt-1">Under review</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-green-50 border-green-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Approved</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">967</div>
                      <p className="text-xs text-muted-foreground mt-1">This month</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-red-50 border-red-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Denied</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">65</div>
                      <p className="text-xs text-muted-foreground mt-1">This month</p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="flex items-center space-x-2 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                    <input 
                      type="text" 
                      placeholder="Search claims by ID, member, or provider..." 
                      className="w-full rounded-md border border-gray-300 pl-9 pr-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                    Search
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    Filters
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium">Claim ID</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Member</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Provider</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Service Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Filed Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">CL-{10000 + i}</td>
                          <td className="px-4 py-3 text-sm">Member Name {i + 1}</td>
                          <td className="px-4 py-3 text-sm">
                            {["General Hospital", "City Medical Center", "Dr. Smith", "Dr. Johnson", "Medical Labs Inc."][i % 5]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(Date.now() - (i + 10) * 24 * 60 * 60 * 1000).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(Date.now() - (i + 5) * 24 * 60 * 60 * 1000).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">${(100 + i * 25).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              i % 4 === 0 ? "bg-blue-100 text-blue-800" : 
                              i % 4 === 1 ? "bg-yellow-100 text-yellow-800" : 
                              i % 4 === 2 ? "bg-green-100 text-green-800" : 
                              "bg-red-100 text-red-800"
                            }`}>
                              {i % 4 === 0 ? "New" : i % 4 === 1 ? "In Process" : i % 4 === 2 ? "Approved" : "Denied"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">View</button>
                              <button className="text-green-600 hover:text-green-800 font-medium">Process</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing 1-10 of 1,245 claims
                  </div>
                  <div className="flex space-x-1">
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">Previous</button>
                    <button className="px-3 py-1 bg-blue-600 text-white rounded-md">1</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">2</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">3</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">Next</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle>Provider Network</CardTitle>
                <CardDescription>Manage healthcare providers in your network</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Provider Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 border rounded-lg">
                          <p className="text-sm font-medium">Physicians</p>
                          <p className="text-2xl font-bold mt-1">642</p>
                          <p className="text-xs text-gray-500 mt-1">75% of network</p>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <p className="text-sm font-medium">Hospitals</p>
                          <p className="text-2xl font-bold mt-1">58</p>
                          <p className="text-xs text-gray-500 mt-1">6.8% of network</p>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <p className="text-sm font-medium">Clinics</p>
                          <p className="text-2xl font-bold mt-1">124</p>
                          <p className="text-xs text-gray-500 mt-1">14.5% of network</p>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <p className="text-sm font-medium">Labs</p>
                          <p className="text-2xl font-bold mt-1">32</p>
                          <p className="text-xs text-gray-500 mt-1">3.7% of network</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Geographic Coverage</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-500">Map visualization would appear here</p>
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Urban Areas:</span>
                          <span className="font-medium">78% coverage</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Suburban Areas:</span>
                          <span className="font-medium">65% coverage</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Rural Areas:</span>
                          <span className="font-medium">42% coverage</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium">Provider ID</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Specialty</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Contract Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Claims Volume</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">P-{10000 + i}</td>
                          <td className="px-4 py-3 text-sm">
                            {i % 3 === 0 ? "Dr. " : i % 3 === 1 ? "Medical Center " : "Hospital "}
                            {["Smith", "Johnson", "Williams", "Brown", "Jones", "Miller", "Davis", "Garcia", "Wilson", "Anderson"][i]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {i % 3 === 0 ? "Physician" : i % 3 === 1 ? "Clinic" : "Hospital"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {["Primary Care", "Cardiology", "Neurology", "Pediatrics", "Orthopedics", "General", "Internal Medicine", "Dermatology", "Oncology", "Emergency"][i]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {["New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ", "Philadelphia, PA", "San Antonio, TX", "San Diego, CA", "Dallas, TX", "San Jose, CA"][i]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${i % 5 === 0 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>
                              {i % 5 === 0 ? "Pending Renewal" : "Active"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">{10 + i * 5} / month</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">View</button>
                              <button className="text-green-600 hover:text-green-800 font-medium">Edit</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing 1-10 of 856 providers
                  </div>
                  <div className="flex space-x-1">
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">Previous</button>
                    <button className="px-3 py-1 bg-blue-600 text-white rounded-md">1</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">2</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">3</button>
                    <button className="px-3 py-1 border rounded-md hover:bg-gray-50">Next</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="plans">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Plans</CardTitle>
                <CardDescription>Manage and configure insurance plan offerings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {[
                    { name: "Gold PPO", members: 4328, premium: "$450" },
                    { name: "Silver HMO", members: 3562, premium: "$350" },
                    { name: "Bronze EPO", members: 2815, premium: "$275" },
                    { name: "Platinum PPO", members: 1254, premium: "$550" },
                    { name: "Gold HMO", members: 886, premium: "$400" }
                  ].map((plan, i) => (
                    <Card key={i} className="border shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle>{plan.name}</CardTitle>
                        <CardDescription>
                          {plan.members.toLocaleString()} enrolled members
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-gray-500">Monthly Premium:</span>
                          <span className="font-medium">{plan.premium}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-gray-500">Annual Revenue:</span>
                          <span className="font-medium">${(Number(plan.premium.replace('$', '')) * plan.members * 12 / 1000000).toFixed(2)}M</span>
                        </div>
                        <div className="mt-4 flex space-x-2">
                          <button className="text-blue-600 text-sm font-medium">Details</button>
                          <button className="text-green-600 text-sm font-medium">Edit</button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-3">Plan Comparison</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Feature</th>
                          <th className="px-4 py-3 text-center text-sm font-medium">Bronze EPO</th>
                          <th className="px-4 py-3 text-center text-sm font-medium">Silver HMO</th>
                          <th className="px-4 py-3 text-center text-sm font-medium">Gold PPO</th>
                          <th className="px-4 py-3 text-center text-sm font-medium">Platinum PPO</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Monthly Premium</td>
                          <td className="px-4 py-3 text-sm text-center">$275</td>
                          <td className="px-4 py-3 text-sm text-center">$350</td>
                          <td className="px-4 py-3 text-sm text-center">$450</td>
                          <td className="px-4 py-3 text-sm text-center">$550</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Annual Deductible</td>
                          <td className="px-4 py-3 text-sm text-center">$5,000</td>
                          <td className="px-4 py-3 text-sm text-center">$3,500</td>
                          <td className="px-4 py-3 text-sm text-center">$1,500</td>
                          <td className="px-4 py-3 text-sm text-center">$500</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Out-of-Pocket Max</td>
                          <td className="px-4 py-3 text-sm text-center">$8,000</td>
                          <td className="px-4 py-3 text-sm text-center">$6,000</td>
                          <td className="px-4 py-3 text-sm text-center">$4,000</td>
                          <td className="px-4 py-3 text-sm text-center">$2,000</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Primary Care Visit</td>
                          <td className="px-4 py-3 text-sm text-center">$50 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$35 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$25 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$15 copay</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Specialist Visit</td>
                          <td className="px-4 py-3 text-sm text-center">$75 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$60 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$50 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$30 copay</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Emergency Room</td>
                          <td className="px-4 py-3 text-sm text-center">$500 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$350 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$250 copay</td>
                          <td className="px-4 py-3 text-sm text-center">$150 copay</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Hospital Stay</td>
                          <td className="px-4 py-3 text-sm text-center">30% coinsurance</td>
                          <td className="px-4 py-3 text-sm text-center">20% coinsurance</td>
                          <td className="px-4 py-3 text-sm text-center">10% coinsurance</td>
                          <td className="px-4 py-3 text-sm text-center">5% coinsurance</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">Prescription Drugs</td>
                          <td className="px-4 py-3 text-sm text-center">$20/$50/$80</td>
                          <td className="px-4 py-3 text-sm text-center">$15/$40/$60</td>
                          <td className="px-4 py-3 text-sm text-center">$10/$30/$50</td>
                          <td className="px-4 py-3 text-sm text-center">$5/$15/$30</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Analytics</CardTitle>
                <CardDescription>Performance reports and key metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Claims Distribution</CardTitle>
                      <CardDescription>By medical specialty</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-500">Pie chart visualization would appear here</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Monthly Claims Volume</CardTitle>
                      <CardDescription>Last 12 months</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-500">Bar chart visualization would appear here</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Key Performance Indicators</CardTitle>
                    <CardDescription>Insurance business metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">Financial Metrics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span>Loss Ratio:</span>
                            <span className="font-medium">72.4%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Medical Cost Ratio:</span>
                            <span className="font-medium">81.2%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Administrative Cost Ratio:</span>
                            <span className="font-medium">12.5%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Revenue per Member:</span>
                            <span className="font-medium">$4,230/year</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">Operational Metrics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span>Claim Processing Time:</span>
                            <span className="font-medium">4.2 days</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>First-Pass Resolution:</span>
                            <span className="font-medium">85.3%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Provider Satisfaction:</span>
                            <span className="font-medium">78%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Member Satisfaction:</span>
                            <span className="font-medium">82%</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">Growth Metrics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span>Member Growth:</span>
                            <span className="font-medium">+12.4% YoY</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Provider Network Growth:</span>
                            <span className="font-medium">+8.7% YoY</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Revenue Growth:</span>
                            <span className="font-medium">+15.2% YoY</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Market Share:</span>
                            <span className="font-medium">23.5%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-3">Available Reports</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { title: "Monthly Financial Summary", icon: <FileText size={20} /> },
                      { title: "Claims Analysis Report", icon: <ClipboardList size={20} /> },
                      { title: "Provider Performance", icon: <Building size={20} /> },
                      { title: "Member Demographics", icon: <Users size={20} /> },
                      { title: "Risk Assessment", icon: <ShieldCheck size={20} /> },
                      { title: "Market Trend Analysis", icon: <Search size={20} /> }
                    ].map((report, i) => (
                      <div key={i} className="p-4 border rounded-lg flex items-center hover:bg-gray-50 cursor-pointer">
                        <div className="mr-3 text-blue-600">
                          {report.icon}
                        </div>
                        <div>
                          <p className="font-medium">{report.title}</p>
                          <p className="text-sm text-gray-500">Last generated: {new Date(Date.now() - i * 3 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default InsurancePage;
